// Funding nakijken (K2, owner-besluit 07-10-2026).
//
// De herhaalronde H1 liet zien dat een herlezing van een ongewijzigde
// subsidiepagina tientallen valse wijzigingen oplevert. De load remt die
// voortaan per veld (K1); wat de rem of de nalezing niet zeker weet, staat hier.
// Per verschil kiest de beheerder: "Nieuw klopt", "Houd oud" of "Zelf invullen".
// Elke volgende load neemt het besluit mee, zolang de pagina niets anders zegt
// dan de oude of de nieuwe waarde (K1 `besluit_geldt`).
//
// Reviewronde 07-10:
//  · een melding (veldtype "geen", bv. bereikbaarheid) heeft geen waarde die de
//    load kan overnemen: alleen "Gezien" (opgeslagen als nieuw_klopt);
//  · een controle die is ingehaald door een nieuwere ronde (`ingehaaldDoor`)
//    krijgt geen knoppen: een besluit erop zou de oude waarde terugzetten;
//  · "oud" is wat live stond bij die ronde, niet per se wat nu live staat.
//
// Wat dit scherm bewust NIET zelf doet:
//  · sorteren of tellen — de server sorteert (risico hoog → laag, dan regeling)
//    en telt over de hele tabel; hier wordt alleen het eigen, nog niet
//    teruggelezen besluit van de tellingen afgetrokken (de optimistische stap);
//  · een keuzelijst of datumregel verzinnen — de soort invoer per veld en de
//    keuzes komen van de server (`velden`); de controle hier is alleen een
//    voorproef, de server beslist en zijn zin wordt letterlijk getoond.
//
// Optimistisch: een klik toont het besluit meteen. Faalt de POST, dan gaat de
// rij terug naar wat hij was en staat de reden eronder. Niets wordt gewist; een
// besluit opnieuw zetten mag (laatste wint).

import { useEffect, useId, useMemo, useState, type CSSProperties, type ReactNode } from "react";

import { Card } from "../../components/ui";
import { EmptyReason, LoadError } from "../../components/Empty";
import { api } from "../../lib/api";
import { useResource } from "../../lib/useResource";
import type {
  FundingControle,
  FundingControlesResponse,
  FundingGenomenBesluit,
  FundingRisico,
  FundingVeldBeschrijving,
} from "../../contracts";
import { color, radius, sectionLabel, size, weight } from "../../tokens";
import { datum, noot } from "./BronnenStukken";
import { FundingVondsten } from "./FundingVondsten";
import { invoer, knopDonker, knopLicht } from "./stijl";
import { leesbaar } from "./woorden";

type Groep = "open" | "afgehandeld";
const RISICOS: readonly FundingRisico[] = ["hoog", "middel", "laag"];

const RISICO_KLEUR: Record<FundingRisico, { tekst: string; vlak: string }> = {
  hoog: { tekst: color.accentRed, vlak: "#fef2f2" },
  middel: { tekst: color.accentAmber, vlak: color.accentAmberBg },
  laag: { tekst: color.soft, vlak: color.lineFaint },
};
const SOORT_KLEUR: Record<string, string> = {
  echte_wijziging: color.accentBlue,
  aanvulling: color.accentGreen,
  leesruis: color.soft,
  onzeker: color.accentAmber,
  bereikbaarheid: color.accentPurple,
  bron: color.accentRed,
};

/** Voorproef van de serverregel (sof.readapi.funding_beheer.valideer_eigen_waarde).
 *  Alleen om een tikfout vóór de klik te melden; de server beslist. */
export function voorproef(type: string, waarde: string, keuzes: string[]): string | null {
  const w = waarde.trim();
  if (!w) return "Vul een waarde in, of kies 'Nieuw klopt' of 'Houd oud'.";
  if (type === "datum") {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(w);
    const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
    if (!m || !d || d.getMonth() !== Number(m[2]) - 1 || d.getDate() !== Number(m[3])) {
      return "Een datum schrijf je als jjjj-mm-dd, bijvoorbeeld 2026-12-31.";
    }
  }
  if (type === "bedrag" && !/^\d+(?:[.,]\d{1,2})?$/.test(w.replace(/[€\s]/g, ""))) {
    return "Een bedrag is een getal zonder punten voor de duizendtallen, bijvoorbeeld 2500 of 2500,50.";
  }
  if (type === "keuze" && !keuzes.includes(w)) return "Kies een waarde uit de lijst.";
  if (type === "tekst" && w.length > 2000) return "Hoogstens 2000 tekens.";
  return null;
}

/** Een waarde zoals een mens hem leest: datum en bedrag op zijn Nederlands, een
 *  keuze met het woord van de server. Leeg = dat, in woorden. */
function Waarde({ waarde, veld }: { waarde: string; veld: FundingVeldBeschrijving | undefined }) {
  if (!waarde) return <EmptyReason reason="leeg" />;
  let tekst = waarde;
  if (veld?.type === "datum") tekst = datum(waarde) ?? waarde;
  else if (veld?.type === "bedrag" && /^\d+(\.\d+)?$/.test(waarde)) {
    tekst = `€ ${Number(waarde).toLocaleString("nl-NL")}`;
  } else if (veld?.type === "keuze") {
    tekst = veld.keuzes.find((k) => k.sleutel === waarde)?.label ?? waarde;
  }
  return <span style={{ color: color.ink, fontWeight: weight.medium }}>{tekst}</span>;
}

function Pil({ tekst, kleur, vlak, label }: { tekst: string; kleur: string; vlak?: string; label?: string }) {
  return (
    <span
      aria-label={label}
      style={{
        background: vlak ?? color.lineFaint,
        color: kleur,
        border: `1px solid ${color.line}`,
        borderRadius: radius.pill,
        padding: "2px 8px",
        fontSize: size.label,
        fontWeight: weight.medium,
        whiteSpace: "nowrap",
      }}
    >
      {tekst}
    </span>
  );
}

type Optimistisch = Record<string, FundingControle>;

/** Het woord voor een besloten melding (gelijk aan sof.readapi.funding_beheer.GEZIEN). */
const GEZIEN = "Gezien";

export function FundingNakijken({ onTelling }: { onTelling?: (open: number) => void }) {
  const [groep, setGroep] = useState<Groep>("open");
  const [risico, setRisico] = useState<"" | FundingRisico>("");
  const lijst = useResource<FundingControlesResponse>(
    () => api.fundingControles({ besluit: groep, risico }),
    [groep, risico],
  );
  // Besluiten die deze beheerder net nam en die nog niet opnieuw van de server
  // zijn gelezen (of waarvan het antwoord al binnen is). Bij een nieuwe lijst weg.
  const [eigen, setEigen] = useState<Optimistisch>({});
  const [fouten, setFouten] = useState<Record<string, string>>({});
  // De rijen waarvan de POST nog loopt: hun knoppen staan even dicht.
  const [bezig, setBezig] = useState<Record<string, boolean>>({});
  const [melding, setMelding] = useState("");
  const kopId = useId();

  useEffect(() => {
    setEigen({});
    setFouten({});
  }, [lijst.data]);

  const d = lijst.data;
  const velden = useMemo(
    () => Object.fromEntries((d?.velden ?? []).map((v) => [v.sleutel, v])),
    [d?.velden],
  );

  // De tellingen van de server, min wat hier net van open naar besloten ging.
  const tel = useMemo(() => {
    if (!d) return null;
    const perRisico = { ...d.tellingen.openPerRisico };
    let open = d.tellingen.open;
    for (const item of d.items) {
      const nu = eigen[item.id];
      if (!nu) continue;
      const was = item.besluit === "open";
      const is = nu.besluit === "open";
      if (was && !is) {
        open -= 1;
        perRisico[item.risico] -= 1;
      } else if (!was && is) {
        open += 1;
        perRisico[item.risico] += 1;
      }
    }
    return { open, perRisico, afgehandeld: d.tellingen.totaal - open };
  }, [d, eigen]);

  useEffect(() => {
    if (tel) onTelling?.(tel.open);
  }, [tel, onTelling]);

  async function besluit(item: FundingControle, keuze: FundingGenomenBesluit, waarde?: string) {
    const label =
      item.veldType === "geen" ? GEZIEN : (d?.besluiten.find((b) => b.sleutel === keuze)?.label ?? keuze);
    setFouten(({ [item.id]: _weg, ...rest }) => rest);
    setEigen((e) => ({
      ...e,
      [item.id]: {
        ...item,
        besluit: keuze,
        besluitLabel: label,
        eigenWaarde: keuze === "eigen_waarde" ? (waarde ?? "").trim() : null,
        besluitDoor: null,
        besluitOp: null,
      },
    }));
    setBezig((b) => ({ ...b, [item.id]: true }));
    setMelding(`Wordt opgeslagen: ${label} — ${item.regeling}, ${item.veldLabel}.`);
    try {
      const terug = await api.fundingControleBesluit(item.id, {
        besluit: keuze,
        eigenWaarde: keuze === "eigen_waarde" ? waarde ?? null : null,
      });
      setEigen((e) => ({ ...e, [item.id]: terug }));
      setMelding(`Opgeslagen: ${label} — ${item.regeling}, ${item.veldLabel}.`);
      return true;
    } catch (err) {
      setEigen(({ [item.id]: _weg, ...rest }) => rest);
      setFouten((f) => ({
        ...f,
        [item.id]: leesbaar(err, "Het besluit kon niet worden opgeslagen — probeer het zo nog eens."),
      }));
      setMelding("");
      return false;
    } finally {
      setBezig(({ [item.id]: _klaar, ...rest }) => rest);
    }
  }

  // Per regeling, in de volgorde van de server (de eerste rij van een regeling
  // bepaalt zijn plek; die volgorde is al hoog → laag risico).
  const groepen = useMemo(() => {
    const per = new Map<string, { kop: FundingControle; items: FundingControle[] }>();
    for (const item of d?.items ?? []) {
      const g = per.get(item.fundingId);
      if (g) g.items.push(item);
      else per.set(item.fundingId, { kop: item, items: [item] });
    }
    return [...per.values()];
  }, [d?.items]);

  return (
    <section aria-labelledby={kopId} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Card style={{ padding: "16px 18px" }}>
        <h3 id={kopId} style={{ ...sectionLabel, marginBottom: 8 }}>
          Funding nakijken
        </h3>
        <p style={{ fontSize: size.meta, color: color.body, lineHeight: 1.55, margin: 0 }}>
          Verschillen tussen wat live stond en de nieuwe lezing van de officiële pagina, die de rem
          bij het laden of de nalezing niet zelf kon beslissen. Je besluit blijft gelden bij elke
          volgende load, ook als de pagina later de nieuwe waarde herhaalt; pas als de pagina iets
          anders zegt, komt er een nieuwe vraag. "Houd oud" zet het veld dus vast tot dan. Niets wordt
          gewist; een besluit kun je opnieuw zetten.
        </p>
        {tel && (
          <dl
            data-tellingen
            style={{ display: "flex", flexWrap: "wrap", gap: "8px 22px", margin: "12px 0 0" }}
          >
            <Telling label="open" waarde={tel.open} sterk />
            {RISICOS.map((r) => (
              <Telling key={r} label={`open, ${r} risico`} waarde={tel.perRisico[r]} kleur={RISICO_KLEUR[r].tekst} />
            ))}
            <Telling label="afgehandeld" waarde={tel.afgehandeld} />
          </dl>
        )}
      </Card>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
        <div
          role="group"
          aria-label="Welke controles"
          style={{
            display: "inline-flex",
            gap: 4,
            background: color.lineFaint,
            borderRadius: radius.input,
            padding: 3,
          }}
        >
          {(["open", "afgehandeld"] as const).map((g) => (
            <button
              key={g}
              type="button"
              className={"view-btn" + (groep === g ? " active" : "")}
              aria-pressed={groep === g}
              onClick={() => setGroep(g)}
            >
              {g === "open" ? "Open" : "Afgehandeld"}
            </button>
          ))}
        </div>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: size.meta, color: color.body }}>
          Risico
          <select
            value={risico}
            onChange={(e) => setRisico(e.target.value as "" | FundingRisico)}
            style={{ ...invoer, width: "auto", padding: "6px 10px", fontSize: size.meta }}
          >
            <option value="">alle</option>
            {RISICOS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
      </div>

      {lijst.error ? (
        <LoadError reason={lijst.error} onRetry={lijst.reload} />
      ) : !d ? (
        <EmptyReason reason="de controles worden opgehaald" />
      ) : d.items.length === 0 ? (
        <Card style={{ padding: "16px 18px" }}>
          <p data-leeg style={{ fontSize: size.body, color: color.body, margin: 0 }}>
            {groep === "open" && !risico ? d.leeg : "Niets in deze selectie."}
          </p>
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, opacity: lijst.stale ? 0.6 : 1 }}>
          {groepen.map(({ kop, items }) => (
            <Card key={kop.fundingId} style={{ padding: "14px 18px" }}>
              <h4 style={{ fontSize: size.body, fontWeight: weight.bold, color: color.ink, margin: 0 }}>
                {kop.regeling || <EmptyReason reason="regeling zonder naam" />}
              </h4>
              <div style={{ fontSize: 12, color: color.soft, marginTop: 2 }}>
                {kop.verstrekker || <EmptyReason reason="verstrekker onbekend" />}
              </div>
              <ul style={{ listStyle: "none", padding: 0, margin: "8px 0 0" }}>
                {items.map((item, i) => (
                  <ControleRij
                    key={item.id}
                    item={eigen[item.id] ?? item}
                    veld={velden[item.veld]}
                    eerste={i === 0}
                    fout={fouten[item.id] ?? null}
                    bezig={Boolean(bezig[item.id])}
                    onBesluit={(keuze, waarde) => besluit(item, keuze, waarde)}
                  />
                ))}
              </ul>
            </Card>
          ))}
        </div>
      )}

      <div role="status" aria-live="polite" data-besluit-status>
        {melding && <p style={{ ...noot, color: color.body }}>{melding}</p>}
      </div>

      {/* Fase 3 (vinden): nieuwe vondsten en 'vervallen?' — eigen component. */}
      <FundingVondsten />
    </section>
  );
}

function Telling({ label, waarde, sterk, kleur }: { label: string; waarde: number; sterk?: boolean; kleur?: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column-reverse" }}>
      <dt style={{ fontSize: 12, color: color.soft }}>{label}</dt>
      <dd
        style={{
          margin: 0,
          fontSize: sterk ? 22 : 18,
          fontWeight: weight.bold,
          color: kleur ?? color.ink,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {waarde}
      </dd>
    </div>
  );
}

function ControleRij({
  item,
  veld,
  eerste,
  fout,
  bezig,
  onBesluit,
}: {
  item: FundingControle;
  veld: FundingVeldBeschrijving | undefined;
  eerste: boolean;
  fout: string | null;
  bezig: boolean;
  onBesluit: (keuze: FundingGenomenBesluit, waarde?: string) => Promise<boolean>;
}) {
  const [invullen, setInvullen] = useState(false);
  const [waarde, setWaarde] = useState(item.eigenWaarde ?? item.waardeNieuw ?? "");
  const [proef, setProef] = useState<string | null>(null);
  const invoerId = useId();
  const foutId = useId();
  const melding = item.veldType === "geen";
  const wie = `${item.veldLabel} van ${item.regeling}`;
  const keuzes = veld?.keuzes ?? [];

  async function opslaan() {
    const p = voorproef(item.veldType, waarde, keuzes.map((k) => k.sleutel));
    setProef(p);
    if (p) return;
    if (await onBesluit("eigen_waarde", waarde)) setInvullen(false);
  }

  const knop = (actief: boolean): CSSProperties => ({
    ...knopLicht,
    padding: "6px 12px",
    background: actief ? color.lineFaint : "none",
    borderColor: actief ? color.ink : color.border,
    color: color.ink,
    cursor: bezig ? "default" : "pointer",
  });

  return (
    <li
      data-controle={item.id}
      aria-busy={bezig || undefined}
      style={{
        padding: "12px 0",
        borderTop: eerste ? "none" : `1px solid ${color.lineFaint}`,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 8px", alignItems: "center" }}>
        <span style={{ fontSize: size.body, fontWeight: weight.medium, color: color.ink }}>
          {item.veldLabel.charAt(0).toUpperCase() + item.veldLabel.slice(1)}
        </span>
        <Pil tekst={item.soortLabel} kleur={SOORT_KLEUR[item.soort] ?? color.soft} label={`soort: ${item.soortLabel}`} />
        <Pil
          tekst={`risico ${item.risico}`}
          kleur={RISICO_KLEUR[item.risico].tekst}
          vlak={RISICO_KLEUR[item.risico].vlak}
        />
        <span style={{ fontSize: 12, color: color.faint }}>
          {item.bronLabel} · {item.runId}
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "8px 18px" }}>
        <Kant kop="Oud, bij deze ronde" waarde={<Waarde waarde={item.waardeOud} veld={veld} />} citaat={item.citaatOud} />
        <Kant kop="Nieuwe lezing" waarde={<Waarde waarde={item.waardeNieuw} veld={veld} />} citaat={item.citaatNieuw} />
      </div>

      {item.uitleg && (
        <p style={{ fontSize: size.meta, color: color.body, lineHeight: 1.5, margin: 0 }}>{item.uitleg}</p>
      )}

      {item.besluit !== "open" && (
        <p data-besloten style={{ fontSize: 12, color: color.soft, margin: 0 }}>
          {`Besloten: ${melding ? GEZIEN : item.besluitLabel}`}
          {item.besluit === "eigen_waarde" && item.eigenWaarde ? ` (${item.eigenWaarde})` : ""}
          {/* 08-10-2026: een lege eigen waarde maakt het veld leeg (CLI
              `funding controles --besluiten`, de load vlagt het). */}
          {item.besluit === "eigen_waarde" && item.eigenWaarde === "" ? " (veld leeggemaakt)" : ""}
          {item.besluitDoor ? ` · door ${item.besluitDoor}` : ""}
          {datum(item.besluitOp) ? ` op ${datum(item.besluitOp)}` : ""}
        </p>
      )}

      {item.ingehaaldDoor ? (
        <p data-ingehaald style={{ fontSize: size.meta, color: color.body, margin: 0 }}>
          {`Ingehaald door ronde ${item.ingehaaldDoor}: daar staat de nieuwste vraag over dit veld. Hier kun je niet meer besluiten; een besluit hier zou de waarde van deze oude ronde terugzetten.`}
        </p>
      ) : melding ? (
        <div role="group" aria-label={`Besluit over ${wie}`} style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
          <button
            type="button"
            disabled={bezig}
            aria-pressed={item.besluit !== "open"}
            onClick={() => void onBesluit("nieuw_klopt")}
            style={knop(item.besluit !== "open")}
          >
            {GEZIEN}
          </button>
          <span style={{ fontSize: 12, color: color.soft }}>
            Een melding over de pagina's, geen waarde: de load doet er niets mee. Is de pagina verhuisd, pas
            dan het register aan.
          </span>
        </div>
      ) : (
        <div role="group" aria-label={`Besluit over ${wie}`} style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          <button
            type="button"
            disabled={bezig}
            aria-pressed={item.besluit === "nieuw_klopt"}
            onClick={() => void onBesluit("nieuw_klopt")}
            style={knop(item.besluit === "nieuw_klopt")}
          >
            Nieuw klopt
          </button>
          <button
            type="button"
            disabled={bezig}
            aria-pressed={item.besluit === "houd_oud"}
            onClick={() => void onBesluit("houd_oud")}
            style={knop(item.besluit === "houd_oud")}
          >
            Houd oud
          </button>
          <button
            type="button"
            disabled={bezig}
            aria-pressed={item.besluit === "eigen_waarde"}
            aria-expanded={invullen}
            onClick={() => {
              setProef(null);
              setInvullen((x) => !x);
            }}
            style={knop(item.besluit === "eigen_waarde")}
          >
            Zelf invullen
          </button>
        </div>
      )}

      {invullen && !melding && !item.ingehaaldDoor && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void opslaan();
          }}
          style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "flex-end" }}
        >
          <label htmlFor={invoerId} style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: color.soft, flex: "1 1 240px" }}>
            {`Eigen waarde voor ${item.veldLabel}`}
            {item.veldType === "keuze" ? (
              <select
                id={invoerId}
                value={waarde}
                onChange={(e) => setWaarde(e.target.value)}
                aria-invalid={proef ? true : undefined}
                aria-describedby={proef ? foutId : undefined}
                style={{ ...invoer, padding: "7px 10px" }}
              >
                <option value="">kies…</option>
                {keuzes.map((k) => (
                  <option key={k.sleutel} value={k.sleutel}>
                    {k.label}
                  </option>
                ))}
              </select>
            ) : item.veldType === "tekst" ? (
              <textarea
                id={invoerId}
                value={waarde}
                maxLength={2000}
                rows={3}
                onChange={(e) => setWaarde(e.target.value)}
                aria-invalid={proef ? true : undefined}
                aria-describedby={proef ? foutId : undefined}
                style={{ ...invoer, padding: "7px 10px", resize: "vertical" }}
              />
            ) : (
              <input
                id={invoerId}
                type="text"
                inputMode={item.veldType === "bedrag" ? "decimal" : undefined}
                placeholder={item.veldType === "datum" ? "jjjj-mm-dd" : "bijvoorbeeld 2500"}
                value={waarde}
                onChange={(e) => setWaarde(e.target.value)}
                aria-invalid={proef ? true : undefined}
                aria-describedby={proef ? foutId : undefined}
                style={{ ...invoer, padding: "7px 10px" }}
              />
            )}
          </label>
          <button type="submit" disabled={bezig} style={knopDonker(bezig)}>
            Opslaan
          </button>
          <button type="button" onClick={() => setInvullen(false)} style={knopLicht}>
            Annuleer
          </button>
          {proef && (
            <p id={foutId} role="alert" style={{ flexBasis: "100%", fontSize: size.meta, color: color.accentRed, margin: 0 }}>
              {proef}
            </p>
          )}
        </form>
      )}

      {fout && (
        <p role="alert" data-fout style={{ fontSize: size.meta, color: color.accentRed, margin: 0 }}>
          {fout}
        </p>
      )}
    </li>
  );
}

function Kant({ kop, waarde, citaat }: { kop: string; waarde: ReactNode; citaat: string }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11, color: color.faint, textTransform: "uppercase", letterSpacing: "0.06em" }}>{kop}</div>
      <div style={{ fontSize: size.body, marginTop: 2 }}>{waarde}</div>
      {citaat ? (
        <blockquote
          style={{
            margin: "6px 0 0",
            padding: "6px 10px",
            background: color.surfaceQuote,
            borderLeft: `3px solid ${color.border}`,
            borderRadius: radius.quote,
            fontSize: 12,
            color: color.body,
            lineHeight: 1.5,
          }}
        >
          {`“${citaat}”`}
        </blockquote>
      ) : (
        <div style={{ marginTop: 4 }}>
          <EmptyReason reason="geen citaat" />
        </div>
      )}
    </div>
  );
}
